import type {
  Movement,
  MovementCategory,
} from "@vlab-corporation/vlab-pilates-service-interface/movement";
import type { BranchPolicy } from "@vlab-corporation/vlab-pilates-service-interface/branch";
import type { JournalTemplate } from "@vlab-corporation/vlab-pilates-service-interface/class";
import type {
  ID,
  DateString,
  YearMonth,
} from "@vlab-corporation/vlab-pilates-service-interface/types";
import type { Member } from "@vlab-corporation/vlab-pilates-service-interface/customer";
import type { JournalDocument } from "@/services/firestore/journals";
import type { ClassCompletionDocument } from "@/services/firestore/classCompletions";
import type { SettlementDocument } from "@/services/firestore/settlements";
import type { ClassWithCompletion } from "@/hooks/useClasses";
import {
  DEMO_MOVEMENTS,
  DEMO_MEMBERS,
  DEMO_REGULAR_CLASSES,
  DEMO_OT_CLASSES,
  DEMO_JOURNALS,
  DEMO_CLASS_COMPLETIONS,
  DEMO_SETTLEMENTS,
  DEMO_BRANCH_POLICY,
  DEMO_JOURNAL_TEMPLATES,
  DEMO_STAFFS,
  DEMO_PRODUCTS,
} from "./demoData";

// ── localStorage keys ──

const STORAGE_PREFIX = "demo_";
const KEYS = {
  journals: `${STORAGE_PREFIX}journals`,
  completions: `${STORAGE_PREFIX}completions`,
  settlements: `${STORAGE_PREFIX}settlements`,
  initialized: `${STORAGE_PREFIX}initialized`,
} as const;

// ── Helpers ──

function loadFromStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function saveToStorage<T>(key: string, data: T): void {
  localStorage.setItem(key, JSON.stringify(data));
}

// ── Initialization ──

export function initDemoStore(): void {
  saveToStorage(KEYS.journals, DEMO_JOURNALS);
  saveToStorage(KEYS.completions, DEMO_CLASS_COMPLETIONS);
  saveToStorage(KEYS.settlements, DEMO_SETTLEMENTS);
  saveToStorage(KEYS.initialized, true);
}

export function isDemoStoreInitialized(): boolean {
  return loadFromStorage<boolean>(KEYS.initialized) === true;
}

export function clearDemoStore(): void {
  Object.values(KEYS).forEach((key) => localStorage.removeItem(key));
}

export function resetDemoStore(): void {
  clearDemoStore();
  initDemoStore();
}

// ── Movements (read-only, in-memory) ──

export function getMovements(_centerId: number): Movement[] {
  return [...DEMO_MOVEMENTS];
}

// ── Members (read-only, in-memory) ──

export function getMembersList(_centerId: number): Member[] {
  return [...DEMO_MEMBERS];
}

export function getMembers(_centerId: number): Map<string, Member> {
  const map = new Map<string, Member>();
  for (const member of DEMO_MEMBERS) {
    map.set(member.name, member);
  }
  return map;
}

export function getMovementsByCategory(_centerId: number, category: MovementCategory): Movement[] {
  return DEMO_MOVEMENTS.filter((m) => m.category === category);
}

// ── Journals (localStorage-backed) ──

function loadJournals(): (JournalDocument & { id: string })[] {
  return loadFromStorage<(JournalDocument & { id: string })[]>(KEYS.journals) ?? DEMO_JOURNALS;
}

function saveJournals(journals: (JournalDocument & { id: string })[]): void {
  saveToStorage(KEYS.journals, journals);
}

export function getJournalByClassId(
  _centerId: number,
  classId: ID,
  instructorId?: ID,
): (JournalDocument & { id: string }) | null {
  const journal = loadJournals().find((j) => j.classId === classId) ?? null;
  if (journal && instructorId && journal.instructorId !== instructorId) return null;
  return journal;
}

export function getJournalById(
  _centerId: number,
  journalId: string,
): (JournalDocument & { id: string }) | null {
  return loadJournals().find((j) => j.id === journalId) ?? null;
}

export function getJournalsByMemberId(
  _centerId: number,
  studiomateMemberId: number,
): (JournalDocument & { id: string })[] {
  return loadJournals()
    .filter((j) => j.studiomateMemberId === studiomateMemberId)
    .sort((a, b) => {
      const aDate = typeof a.createdAt === "string" ? a.createdAt : "";
      const bDate = typeof b.createdAt === "string" ? b.createdAt : "";
      return bDate.localeCompare(aDate);
    });
}

export function saveJournal(
  _centerId: number,
  data: Omit<JournalDocument, "createdAt" | "updatedAt">,
): string {
  if (!data.exercises || data.exercises.length === 0) {
    throw new Error("Journal must have at least one exercise");
  }
  const journals = loadJournals();
  const id = `demo-journal-${Date.now()}`;
  const now = new Date().toISOString();
  journals.push({ id, ...data, createdAt: now, updatedAt: now });
  saveJournals(journals);
  return id;
}

export function updateJournal(
  _centerId: number,
  journalId: string,
  data: Partial<
    Pick<
      JournalDocument,
      | "classType"
      | "isOt"
      | "classFormat"
      | "equipments"
      | "exercises"
      | "problemList"
      | "notes"
      | "bodyAnalysis"
      | "memberGoal"
      | "instructorMemo"
    >
  >,
): void {
  if (data.exercises && data.exercises.length === 0) {
    throw new Error("Journal must have at least one exercise");
  }
  const journals = loadJournals();
  const idx = journals.findIndex((j) => j.id === journalId);
  if (idx === -1) throw new Error("Journal not found");
  journals[idx] = { ...journals[idx], ...data, updatedAt: new Date().toISOString() };
  saveJournals(journals);
}

// ── Class Completions (localStorage-backed) ──

function loadCompletions(): ClassCompletionDocument[] {
  return loadFromStorage<ClassCompletionDocument[]>(KEYS.completions) ?? DEMO_CLASS_COMPLETIONS;
}

function saveCompletions(completions: ClassCompletionDocument[]): void {
  saveToStorage(KEYS.completions, completions);
}

export function getClassCompletion(_centerId: number, classId: ID): ClassCompletionDocument {
  const found = loadCompletions().find((c) => c.classId === classId);
  return found ?? { classId, journalWritten: false, updatedAt: null };
}

export function updateClassCompletion(
  _centerId: number,
  classId: ID,
  journalWritten: boolean,
  journalId?: string,
): void {
  const completions = loadCompletions();
  const idx = completions.findIndex((c) => c.classId === classId);
  const entry: ClassCompletionDocument = {
    classId,
    journalWritten,
    ...(journalId ? { journalId } : {}),
    updatedAt: new Date().toISOString(),
  };
  if (idx >= 0) {
    completions[idx] = entry;
  } else {
    completions.push(entry);
  }
  saveCompletions(completions);
}

// ── Settlements (localStorage-backed) ──

function loadSettlements(): SettlementDocument[] {
  return loadFromStorage<SettlementDocument[]>(KEYS.settlements) ?? DEMO_SETTLEMENTS;
}

export function getSettlement(_centerId: number, yearMonth: YearMonth): SettlementDocument | null {
  return loadSettlements().find((s) => s.yearMonth === yearMonth) ?? null;
}

export function saveSettlement(
  _centerId: number,
  data: Omit<SettlementDocument, "calculatedAt">,
): void {
  const settlements = loadSettlements();
  const idx = settlements.findIndex((s) => s.yearMonth === data.yearMonth);
  const entry: SettlementDocument = { ...data, calculatedAt: null };
  if (idx >= 0) {
    settlements[idx] = entry;
  } else {
    settlements.push(entry);
  }
  saveToStorage(KEYS.settlements, settlements);
}

// ── Branch Policy (read-only, in-memory) ──

export function getBranchPolicy(_centerId: number): BranchPolicy {
  return { ...DEMO_BRANCH_POLICY };
}

// ── Journal Templates (read-only, in-memory) ──

export function getJournalTemplates(_centerId: number): JournalTemplate[] {
  return [...DEMO_JOURNAL_TEMPLATES];
}

// ── Classes for useClasses hook ──

export function getClassesForDate(date: DateString, instructorId: ID): ClassWithCompletion[] {
  const completions = loadCompletions();
  const completionMap = new Map(completions.map((c) => [c.classId, c]));

  const regulars: ClassWithCompletion[] = DEMO_REGULAR_CLASSES.filter(
    (c) => c.date === date && c.instructorId === instructorId,
  ).map((c) => ({
    id: c.id,
    type: "regular" as const,
    date: c.date,
    status: c.status,
    instructorId: c.instructorId,
    memberId: c.memberId,
    membershipId: c.membershipId,
    journalWritten: completionMap.get(c.id)?.journalWritten ?? false,
  }));

  const ots: ClassWithCompletion[] = DEMO_OT_CLASSES.filter(
    (c) => c.date === date && c.instructorId === instructorId,
  ).map((c) => ({
    id: c.id,
    type: "ot" as const,
    date: c.date,
    status: c.status,
    instructorId: c.instructorId,
    leadId: c.leadId,
    journalWritten: completionMap.get(c.id)?.journalWritten ?? false,
  }));

  return [...regulars, ...ots];
}

// ── Studiomate Mock Data (read-only, in-memory) ──

export function getStudiomateStaffs() {
  return { staffs: DEMO_STAFFS };
}

export function getStudiomateProducts() {
  return [...DEMO_PRODUCTS];
}
